import React, { useCallback, useEffect, useRef, useState } from "react";
import "./index.scss";
import "../../../assets/styles/calculator.scss";
import { Amount, Button, Icon, Link, Select, Text } from "@mbank-design/design-system/components";
import { FontColor, FontSize, FontWeight, LineHeight } from "@mbank-design/design-system/design-tokens/general/typography";
import HorizontalRadioBox from "../../parts/HorizontalRadioBox";
import { Envelope, Lock, PhoneHandle } from "@mbank-design/design-system/icons";
import CalculatorMobileBaner from "../../parts/CalculatorMobileBaner";
import { numberWithSpaces } from "../../common/number-with-spaces";
import { printOffer } from "../../../services";
import { handleClickCalc } from "../../../scripts/commons/common";
import { ButtonType, IconColor, TextAlign } from "@mbank-design/design-system/enums";
import ReservedLabel from "../../parts/ReservedLabel";
import { IS_COMPANY_MOCKED_OPTIONS } from "../../../constants";
import { useCalculatorScrollEdges } from "./hooks/useCalculatorScrollEdges";
import { useHideMobileCtaNearFooter } from "./hooks/useHideMobileCtaNearFooter";
import { useCalculatorContext } from "./DetailsCalculatorProvider";
import { getInitialIsCompany } from "./utils/getInitialIsCompany";
import { CalculatorBottomSkeleton, CalculatorSkeleton } from "./CalculatorSkeleton";
import { CalculatorPromoCodeBar } from "./CalculatorPromoCodeBar";

const formatNettoAdditionalInfo = (price: number | undefined | null): string | null =>
  price != null && price !== 0 ? `${numberWithSpaces(price)} zł netto` : null;

const getCompanyMockedOptions = (isCompany: boolean) =>
  IS_COMPANY_MOCKED_OPTIONS.map((option) => ({
    ...option,
    Status: option.Value === "isCompany" ? isCompany : !isCompany
  }));

export interface CalculatorReworkProps {
  pageType: string;
  literals: any;
  carId: number;
  reservation: boolean;
  availableAttracivenessLevels: any;
  currentLevel: string;
  currentProvision: number;
  levelChangeHandler: (value: string) => void;
  handleContactModal: (url: string) => void;
  setToggleRetailModalForm: () => void;
  disableApplication: boolean;
  isProgramMyElectric: boolean;
  isLeasingAvailable: boolean;
  isRentAvailable: boolean;
  isCashAvailable: boolean;
  totalPriceNetto: number;
  totalPriceBrutto: number;
}

export const CalculatorRework = (props: CalculatorReworkProps) => {
  const {
    pageType,
    literals,
    reservation,
    availableAttracivenessLevels,
    currentLevel,
    currentProvision,
    levelChangeHandler,
    handleContactModal,
    setToggleRetailModalForm,
    disableApplication,
    isProgramMyElectric,
    isLeasingAvailable,
    isRentAvailable,
    isCashAvailable,
    totalPriceNetto,
    totalPriceBrutto
  } = props;

  const urlParams = new URLSearchParams(window.location.search);
  const floatingWrapperRef = useRef<HTMLDivElement>(null);
  const { mobileBannerClassName, priceContainerClassName } = useCalculatorScrollEdges(floatingWrapperRef);
  const hideCTAMobile = useHideMobileCtaNearFooter();

  const [isCompany, setIsCompany] = useState(getInitialIsCompany);
  const [printingId, setPrintingId] = useState("");
  const [pdfGenerationInProgress, setPdfGenerationInProgress] = useState(false);
  const calculator = useCalculatorContext();
  const {
    isLoading,
    isError,
    showCalc,
    setShowCalc,
    activeTab,
    persistActiveTab,
    rentCalculatorData,
    leasingCalculatorData,
    handleRentRadioChange,
    handleLeasingRadioChange,
    packageTiles,
    installmentPrice,
    installmentPriceWithoutPackages,
    selectedPrices,
    urlObject,
    printOfferPayload,
    carState,
    vendorType,
    promoCodeInput,
    setPromoCodeInput,
    applyPromoCode,
    promoCodeMessage,
    isPromoCodeApplied,
    isPromoCodeLoading,
    promoCodeStatus
  } = calculator;

  const isCompanyMockedOptions = getCompanyMockedOptions(isCompany);

  useEffect(() => {
    setPrintingId(urlParams.get("printingId") || "");
  }, []);

  useEffect(() => {
    if (!isCompany && isCashAvailable && activeTab !== 3) {
      persistActiveTab(3);
    }
  }, [isCompany, isCashAvailable, activeTab, persistActiveTab]);

  const handleActiveTabChange = (tab: number) => {
    if (isCompany || tab === 3 || (tab === 1 && !isRentAvailable)) {
      persistActiveTab(tab);
    }
  };

  const handleIsCompanyChange = (nextIsCompany: boolean) => {
    if (!nextIsCompany) {
      handleActiveTabChange(3);
    }
    setIsCompany(nextIsCompany);
  };

  const setLocalStorageContactPreference = () => {
    localStorage.setItem("onlyOffer", JSON.stringify(true));
  };

  const handlePrintOffer = useCallback(() => {
    if (pdfGenerationInProgress || !printingId || !printOfferPayload) {
      return;
    }

    setPdfGenerationInProgress(true);
    printOffer(printingId, printOfferPayload)
      .then((res) => {
        if (res.data.base64 && res.data.fileName) {
          const downloadLink = document.createElement("a");
          downloadLink.href = res.data.base64;
          downloadLink.download = res.data.fileName;
          downloadLink.click();
        }

        if (res.data.errorCode && res.data.errorMessage) {
          alert(res.data.errorMessage);
        }
      })
      .finally(() => {
        setPdfGenerationInProgress(false);
      });
  }, [pdfGenerationInProgress, printingId, printOfferPayload]);

  const isBrokerView = Boolean(urlParams.get("vendorType"));
  const isPromoCodeBarVisible = !isBrokerView && !reservation;

  const promoCodeBar = isPromoCodeBarVisible && (
    <CalculatorPromoCodeBar
      promoCode={promoCodeInput}
      setPromoCode={setPromoCodeInput}
      applyPromoCode={applyPromoCode}
      message={promoCodeMessage}
      success={isPromoCodeApplied}
      isLoading={isPromoCodeLoading}
      status={promoCodeStatus}
    />
  );

  return (
    <>
      <div className={`cta-mobile ${hideCTAMobile && "hide-cta"}`} id="cta-mobile">
        <div className="cta-mobile__price-container">
          {installmentPrice !== undefined && (
            <>
              <Text as={"p"} styledAs={"small"}>rata netto od</Text>
              <Text as="p" styledAs="h2" color={FontColor.DARK} align={TextAlign.RIGHT}>
                <Amount
                  isFractionPartShown={false}
                  currencyDisplay="symbol"
                  currency="PLN"
                  isFractionPartBolded={true}
                  isFractionPartResized={false}
                  value={installmentPrice}
                />
              </Text>
            </>
          )}
        </div>
        <div className="cta-mobile__button-container">
          <a href={literals && `tel:${literals.callusbox_tel.replace(/ /g, "")}`}>
            <Button buttonType="secondary">zadzwoń</Button>
          </a>
          <Link onClick={() => handleClickCalc()}>
            <Button>kalkulator</Button>
          </Link>
        </div>
      </div>
      <div className={"floating-wrapper"} id="floating-wrapper" ref={floatingWrapperRef}>
        {reservation && !showCalc && (
          <div className="reservation-info-container">
            <ReservedLabel text={"zarezerwowany"} iconType={Lock} color={FontColor.DARK} textAs="p" />
            <Text as="h2" marginTop={16} marginBottom={24}>szukasz podobnego auta?</Text>
            <div className="tiles-wrapper">
              <div>
                <Icon iconComponent={PhoneHandle} primaryColor={IconColor.PRIMARY_CONTRAST} />
                <Text as="h3" marginTop={8} fontWeight={FontWeight.WEIGHT_600}>zadzwoń</Text>
                <Text as="p" marginBottom={8}>{literals && literals.callusbox_time}</Text>
                <a href={literals && `tel:${literals.callusbox_tel}`}>{literals && literals.callusbox_tel}</a>
              </div>
              <div>
                <Icon iconComponent={Envelope} primaryColor={IconColor.PRIMARY_CONTRAST} />
                <Text as="h3" marginTop={8} fontWeight={FontWeight.WEIGHT_600}>napisz</Text>
                <Text as="p" marginBottom={8}>odpowiedź w 24h</Text>
                <Link href={`/contactForm?${urlObject}`}>skontaktuj się</Link>
              </div>
            </div>
          </div>
        )}
        {reservation && !showCalc && (
          <div className="launch-preview-box">
            <Text as="h3">finansowanie</Text>
            <Text as="p" marginBottom={8}>zobacz ile kosztowałby Cię ten samochód</Text>
            <Button buttonType={ButtonType.TERTIARY} onClick={() => setShowCalc(true)}>
              oblicz ratę miesieczną
            </Button>
          </div>
        )}
        <div
          id="calculator-container"
          className={`calculator-container${carState === "new" ? " new-car-calculator-container" : ""}${isLoading ? " calculator-container--loading" : ""} ${!reservation || (reservation && showCalc) ? "block" : "none"}`}
        >
          <CalculatorMobileBaner classes={mobileBannerClassName} />
          {reservation && (
            <ReservedLabel text={"zarezerwowany"} iconType={Lock} color={FontColor.DARK} textAs="p" />
          )}
          <div className="calculator-header">
            {availableAttracivenessLevels ? (
              <Select
                items={availableAttracivenessLevels}
                onChange={(value: string | string[]) => levelChangeHandler(value as string)}
                value={currentLevel ?? "0"}
                label={"poziom atrakcyjności"}
              />
            ) : (
              <Text
                as="p"
                fontWeight={FontWeight.WEIGHT_600}
                fontSize={[FontSize.SIZE_20, FontSize.SIZE_24]}
                //@ts-ignore
                lineHeight={[24, 28]}
                className="hide-on-mobile"
              >
                {literals && literals.prod_used_calc_hdr}
              </Text>
            )}
            {isCashAvailable === true && (
              <HorizontalRadioBox
                RadioName={"isCompany"}
                Options={isCompanyMockedOptions}
                handler={(e: any) => handleIsCompanyChange(e.Value === "isCompany")}
                Size={"normal"}
              />
            )}
            {isCashAvailable === false && <Text as="p">oferta tylko dla firm</Text>}
          </div>
          {isCompany && (
            <div className={`tabs-container ${!isRentAvailable ? "two-el-only" : ""}`}>
              {isRentAvailable && (
                <div className={`tab ${activeTab === 1 ? "active" : ""}`} onClick={() => handleActiveTabChange(1)}>
                  <Text as="p" fontWeight={FontWeight.WEIGHT_600}>najem</Text>
                </div>
              )}
              {isLeasingAvailable && (
                <div className={`tab ${activeTab === 2 ? "active" : ""}`} onClick={() => handleActiveTabChange(2)}>
                  <Text as="p" fontWeight={FontWeight.WEIGHT_600}>leasing</Text>
                </div>
              )}
              {isCashAvailable && (
                <div className={`tab ${activeTab === 3 ? "active" : ""}`} onClick={() => handleActiveTabChange(3)}>
                  <Text as="p" fontWeight={FontWeight.WEIGHT_600}>gotówka</Text>
                </div>
              )}
              {availableAttracivenessLevels && currentProvision && (
                <div className="provision-container">prowizja {currentProvision}%</div>
              )}
            </div>
          )}
          <div className={activeTab !== 3 ? "content-container" : "content-container content-container-cash"}>
            {isLoading ? (
              <>
                {activeTab === 3 && promoCodeBar}
                <CalculatorSkeleton activeTab={activeTab} />
              </>
            ) : (
              <>
            {activeTab === 1 && rentCalculatorData?.Periods && rentCalculatorData.Ows && rentCalculatorData.Mileages && (
              <>
                <HorizontalRadioBox
                  RadioName={"Periods"}
                  Options={rentCalculatorData.Periods}
                  Label={"liczba miesięcy najmu"}
                  Parent={"LongTimeRentCalculatorValuesDTO"}
                  handler={handleRentRadioChange}
                  Size={"normal"}
                />
                <HorizontalRadioBox
                  RadioName={"Ows"}
                  Options={
                    carState === "new"
                      ? rentCalculatorData.Ows.map((ows: any) => {
                          ows.Name = ows.Price + " zł";
                          return ows;
                        })
                      : rentCalculatorData.Ows
                  }
                  Label={"opłata wstępna"}
                  handler={handleRentRadioChange}
                  Parent={"LongTimeRentCalculatorValuesDTO"}
                  Size={"normal"}
                  AdditionalInfo={carState !== "new" ? formatNettoAdditionalInfo(selectedPrices?.owPrice) : null}
                />
                <HorizontalRadioBox
                  RadioName={"Mileages"}
                  Options={rentCalculatorData.Mileages}
                  Label={"przebieg roczny (tys. km)"}
                  handler={handleRentRadioChange}
                  Parent={"LongTimeRentCalculatorValuesDTO"}
                  Size={"normal"}
                />
              </>
            )}
            {activeTab === 2 && leasingCalculatorData?.Periods && leasingCalculatorData.Ows && leasingCalculatorData.Rvs && (
              <div className="leasing-container">
                <HorizontalRadioBox
                  RadioName={"Periods"}
                  Options={leasingCalculatorData.Periods}
                  Label={"liczba miesięcy leasingu"}
                  handler={handleLeasingRadioChange}
                  Parent={"LeasingCalculatorValues"}
                  Size={"normal"}
                />
                <HorizontalRadioBox
                  RadioName={"Ows"}
                  Options={leasingCalculatorData.Ows}
                  Label={"opłata wstępna"}
                  handler={handleLeasingRadioChange}
                  Parent={"LeasingCalculatorValues"}
                  Size={"normal"}
                  AdditionalInfo={formatNettoAdditionalInfo(selectedPrices?.owPrice)}
                />
                <HorizontalRadioBox
                  RadioName={"Rvs"}
                  Options={leasingCalculatorData.Rvs}
                  Label={"wykup"}
                  handler={handleLeasingRadioChange}
                  Parent={"LeasingCalculatorValues"}
                  Size={"normal"}
                  AdditionalInfo={formatNettoAdditionalInfo(selectedPrices?.rvValuePLN)}
                />
              </div>
            )}
            {activeTab !== 3 && (
              <div>
                <div className="price-overview">
                  {carState !== "new" && activeTab !== 1 && (
                    <div>
                      <div>
                        <Text fontSize={FontSize.SIZE_12} as={"p"} fontWeight={FontWeight.WEIGHT_600} lineHeight={LineHeight.HEIGHT_16}>
                          wartość samochodu {isCompany ? "netto" : "brutto"}
                        </Text>
                      </div>
                      <div className="amount-container">
                        {!isLoading && !isError && (
                          <Text as={"p"} styledAs={"h4"} color={FontColor.MONO_600}>
                            <Amount
                              isFractionPartShown={false}
                              currencyDisplay="symbol"
                              currency="PLN"
                              value={isCompany ? totalPriceNetto : totalPriceBrutto}
                            />
                          </Text>
                        )}
                      </div>
                    </div>
                  )}
                  {activeTab !== 1 && (
                    <div>
                      <div className="price__netto-text">
                        <Text fontSize={FontSize.SIZE_12} as={"p"} fontWeight={FontWeight.WEIGHT_600} lineHeight={LineHeight.HEIGHT_16}>
                          rata finansowa
                        </Text>
                      </div>
                      <div className="amount-container">
                        {installmentPriceWithoutPackages != null && (installmentPriceWithoutPackages > 0) && (
                          <Text as={"p"} styledAs={"h4"} marginRight={16}>
                            <Amount isFractionPartShown={false} currencyDisplay="symbol" currency="PLN" value={installmentPriceWithoutPackages} />
                          </Text>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className={`price-container ${priceContainerClassName}`} />
              </div>
            )}
            {activeTab !== 3 && activeTab !== 4 && (
              <div className="rent-container">
                <div className="additional-packages-container">
                  <div className={`collapsing-container active`}>
                    <div className="content">{packageTiles}</div>
                  </div>
                </div>
              </div>
            )}
            {activeTab === 3 && (
              <>
                <div className="cash-container">
                  <Text as="p" styledAs="h3" fontSize={FontSize.SIZE_24} fontWeight={FontWeight.WEIGHT_600} marginBottom={16} lineHeight={LineHeight.HEIGHT_32}>
                    {literals && literals.calc_used_cash_hdr}
                  </Text>
                </div>
                {promoCodeBar}
                <div className={`price-container price-container--cash ${priceContainerClassName}`}>
                  <div className="price">
                    <div>
                      <Text fontSize={FontSize.SIZE_12} as={"p"} fontWeight={FontWeight.WEIGHT_600} lineHeight={LineHeight.HEIGHT_16}>
                        wartość samochodu {isCompany ? "netto" : "brutto"}
                      </Text>
                    </div>
                    <div>
                      {!isLoading && !isError && (
                        <Text as={"p"} fontSize={FontSize.SIZE_12} fontWeight={FontWeight.WEIGHT_600}>
                          <Amount
                            isFractionPartShown={false}
                            currencyDisplay="symbol"
                            currency="PLN"
                            value={isCompany ? totalPriceNetto : totalPriceBrutto}
                          />
                        </Text>
                      )}
                    </div>
                  </div>
                  <div className="calc-buttons-container">
                    <a href={literals && `tel:${literals.callusbox_tel.replace(/ /g, "")}`} className="calc-buttons-container__call">
                      <Button buttonType="secondary">zadzwoń</Button>
                    </a>
                    <Button buttonType="primary" onClick={() => { setLocalStorageContactPreference(); handleContactModal(urlObject); }}>
                      {literals && literals.calc_used_cash_cta}
                    </Button>
                    {printingId && (
                      <Button buttonType={ButtonType.SECONDARY} onClick={handlePrintOffer}>
                        {pdfGenerationInProgress ? "proszę czekać" : "generuj pdf"}
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
              </>
            )}
          </div>
          {activeTab !== 3 && (
            <>
            {promoCodeBar}
            <div className={`price-container price-container--bottom ${priceContainerClassName}`}>
              {isLoading ? (
                <CalculatorBottomSkeleton />
              ) : (
              <>
              <div className="price price__summary">
                <div>
                  <div className="price__netto-text price__netto-text--big">
                    {isProgramMyElectric ? (
                      <Text fontSize={FontSize.SIZE_12} as={"p"} fontWeight={FontWeight.WEIGHT_600} lineHeight={LineHeight.HEIGHT_16}>
                        rata miesięczna netto z dotacją „<a href={`/elektromobilnosc`}>Nasze auto</a>” przy odliczeniu 100% VAT
                      </Text>
                    ) : (
                      <Text fontSize={FontSize.SIZE_12} as={"p"} fontWeight={FontWeight.WEIGHT_600} lineHeight={LineHeight.HEIGHT_16}>
                        rata łącznie netto
                      </Text>
                    )}
                  </div>
                  <div className="amount-container">
                    {!isLoading && !isError && installmentPrice !== undefined && (
                      <Text as="p" styledAs="h1" color={FontColor.DARK}>
                        <Amount isFractionPartShown={false} currencyDisplay="symbol" currency="PLN" value={installmentPrice} />
                      </Text>
                    )}
                  </div>
                </div>
              </div>
              <div className={"calc-buttons-container"}>
                {!reservation &&
                  vendorType != "2" &&
                  (carState === "new" || (pageType === "afterLease" && !disableApplication)) && (
                    <Button buttonType="primary" onClick={() => { setToggleRetailModalForm(); handleContactModal(urlObject); }}>
                      złóż wniosek
                    </Button>
                  )}
                {vendorType != "2" && (
                  <Button
                    onClick={() => { setLocalStorageContactPreference(); handleContactModal(urlObject); }}
                    buttonType={carState === "new" || (pageType === "afterLease" && !disableApplication) ? "secondary" : "primary"}
                  >
                    zapytaj o ofertę
                  </Button>
                )}
                {printingId && (
                  <Button buttonType={ButtonType.SECONDARY} onClick={handlePrintOffer}>
                    {pdfGenerationInProgress ? "proszę czekać" : "generuj pdf"}
                  </Button>
                )}
              </div>
              </>
              )}
            </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};
